import { URL_CONSTANTS } from "../_constants/url.constants";
import { ServiceHelper } from "./ServiceHelper";

export const parkingService = {
    getParkingConfig,
    setParkingConfig,

    getParkingSpots,
    getParkingSpot,
    createParkingSpot,
    bulkCreateParkingSpots,
    updateParkingSpot,
    deleteParkingSpot,
    clearAllParkingSpots,
    assignHolder,
    addSpotAssignment,
    removeSpotAssignment,
};

function getParkingConfig(locationUid: string) {
    return ServiceHelper.getRequest(URL_CONSTANTS.PARKING_CONFIG_INFO, {
        loc_uid: locationUid,
    });
}

function setParkingConfig({
    loc_uid,
    parking_invitation_group_uid,
}: {
    loc_uid: string;
    parking_invitation_group_uid: string | null;
}) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_CONFIG_UPDATE, {
        loc_uid,
        parking_invitation_group_uid,
    });
}

interface GetSpotsParams {
    loc_uid: string;
    offset?: number; // page number (matches project convention)
    page_size?: number;
    floor?: string | null;
    status?: 'ALL' | 'FREE' | 'OCCUPIED';
    search?: string;
    holder_email?: string | null;
}

function getParkingSpots(params: GetSpotsParams) {
    const query: Record<string, string | number> = { loc_uid: params.loc_uid };
    if (params.offset !== undefined) query.offset = params.offset;
    if (params.page_size !== undefined) query.page_size = params.page_size;
    if (params.floor) query.floor = params.floor;
    if (params.status) query.status = params.status;
    if (params.search) query.search = params.search;
    if (params.holder_email) query.holder_email = params.holder_email;
    return ServiceHelper.getRequest(URL_CONSTANTS.PARKING_SPOT_LIST, query);
}

function getParkingSpot(uid: string) {
    return ServiceHelper.getRequest(URL_CONSTANTS.PARKING_SPOT_INFO, { uid });
}

interface CreateSpotPayload {
    loc_uid: string;
    floor: string;
    number_raw: string;
    holder_name?: string;
    holder_email?: string;
    max_assignments?: number;
    notes?: string;
}

function createParkingSpot(payload: CreateSpotPayload) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_CREATE, {
        loc_uid: payload.loc_uid,
        floor: payload.floor,
        number: payload.number_raw,
        holder_name: payload.holder_name,
        holder_email: payload.holder_email,
        max_assignments: payload.max_assignments,
        notes: payload.notes,
    });
}

interface BulkCreatePayload {
    loc_uid: string;
    rows: Array<{
        holder_name?: string;
        holder_email?: string;
        floor: string;
        number_raw: string;
        max_assignments?: number;
    }>;
}

function bulkCreateParkingSpots({ loc_uid, rows }: BulkCreatePayload) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_BULK_CREATE, {
        loc_uid,
        rows: rows.map((r) => ({
            holder_name: r.holder_name,
            holder_email: r.holder_email,
            floor: r.floor,
            number: r.number_raw,
            max_assignments: r.max_assignments,
        })),
    });
}

interface UpdateSpotPayload {
    uid: string;
    holder_name?: string | null;
    holder_email?: string | null;
    holder_user_uid?: string | null;
    holder_invitation_uid?: string | null;
    max_assignments?: number;
    notes?: string;
}

function updateParkingSpot(payload: UpdateSpotPayload) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_UPDATE, {
        uid: payload.uid,
        holder_name: payload.holder_name,
        holder_email: payload.holder_email,
        max_assignments: payload.max_assignments,
        notes: payload.notes,
    });
}

function deleteParkingSpot(uid: string) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_REMOVE, { uid });
}

function clearAllParkingSpots(loc_uid?: string) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_CLEAR, {
        loc_uid,
    });
}

function assignHolder(payload: UpdateSpotPayload) {
    // Changing holder is a field-level update on the spot; the backend handles
    // the old invitation revocation + new invitation creation internally.
    return updateParkingSpot(payload);
}

function addSpotAssignment({
    uid,
    email,
    user_name,
    begin,
    end,
}: {
    uid: string;
    email: string;
    user_name?: string;
    begin?: string | null;
    end?: string | null;
}) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_ASSIGN, {
        spot_uid: uid,
        email,
        user_name,
        begin,
        end,
    });
}

function removeSpotAssignment({ uid, email }: { uid: string; email: string }) {
    return ServiceHelper.postRequest(URL_CONSTANTS.PARKING_SPOT_UNASSIGN, {
        spot_uid: uid,
        email,
    });
}

